import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Grid,
  IconButton
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import CloseIcon from '@mui/icons-material/Close';
import useApi from '../../hooks/useApi';
import branding from '../../styles/branding';
import { formatBusinessText } from '../../utils/textFormatting';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';
import './BuildBarChart.css';

const KPI_MEASURES = [
  { value: 'count', label: 'Count' },
  { value: 'average', label: 'Average' },
  { value: 'sum', label: 'Sum' },
  { value: 'median', label: 'Median' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' }
];

function getCellValue(row, columnName) {
  if (row[columnName] !== undefined && row[columnName] !== null) return row[columnName];
  const key = Object.keys(row).find(k => String(k).trim().toLowerCase() === String(columnName).trim().toLowerCase());
  return key !== undefined ? row[key] : undefined;
}

function parseNum(val) {
  if (val === null || val === undefined || val === '') return NaN;
  if (typeof val === 'number') return isNaN(val) ? NaN : val;
  const s = String(val).replace(/,/g, '').trim();
  const n = parseFloat(s);
  return s !== '' && !isNaN(n) ? n : NaN;
}

function computeMedian(arr) {
  if (!arr || arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Restore saved SourceColumns to the live label (numeric list first — same as KPI dropdown). */
function matchSavedColumnName(saved, allColumns, numericColumns) {
  if (saved == null || saved === '') return null;
  const lk = String(saved).trim().toLowerCase();
  const fromNumeric = numericColumns.find((c) => String(c).trim().toLowerCase() === lk);
  if (fromNumeric) return fromNumeric;
  return allColumns.find((c) => String(c).trim().toLowerCase() === lk) || null;
}

const BuildKPI = ({ open, onClose, onAddToDashboard, jobId, initialData, editWidgetId, onUpdateCard }) => {
  const [columns, setColumns] = useState([]);
  const [numericColumns, setNumericColumns] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [measure, setMeasure] = useState('average');
  const [widgetName, setWidgetName] = useState('');
  const [subLabel, setSubLabel] = useState('');
  const [isSubLabelDirty, setIsSubLabelDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const api = useApi();
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  /** KPI picker: numeric columns only (same set the backend uses for typical aggregates). */
  const availableColumns = numericColumns.length > 0 ? numericColumns : columns;

  const previewValue = useMemo(() => {
    if (!selectedColumn || chartData.length === 0) return null;
    const colValues = chartData.map(row => getCellValue(row, selectedColumn));
    const nonEmpty = colValues.filter(val => val !== null && val !== undefined && val !== '');
    const numValues = nonEmpty.map(parseNum).filter(n => !isNaN(n));
    if (numValues.length === 0) return null;
    const count = numValues.length;
    const sum = numValues.reduce((a, b) => a + b, 0);
    let value = 0;
    switch (measure) {
      case 'sum': value = sum; break;
      case 'count': value = count; break;
      case 'average': value = count > 0 ? sum / count : 0; break;
      case 'min': value = count > 0 ? Math.min(...numValues) : 0; break;
      case 'max': value = count > 0 ? Math.max(...numValues) : 0; break;
      case 'median': value = computeMedian(numValues); break;
      default: value = count > 0 ? sum / count : 0;
    }
    return typeof value === 'number' ? (Number.isFinite(value) ? value : 0) : value;
  }, [selectedColumn, measure, chartData]);

  const fetchData = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const [jobRes, previewRes] = await Promise.all([
        api.get(`/jobs/${jobId}?getColumns=true`),
        api.get(`/preview/${jobId}?all=true`)
      ]);
      const jobData = jobRes.data?.data ?? jobRes.data;
      const previewData = previewRes.data?.data ?? previewRes.data;
      const cols = jobData?.columns || previewData?.columns?.map(c => c.name || c) || [];
      const numeric = jobData?.numericColumns || [];
      setColumns(cols);
      setNumericColumns(numeric);
      setChartData(previewData?.previewData || previewData?.rows || []);

      const savedCol = initialData?.selectedColumns?.[0];
      if (cols.length > 0) {
        if (savedCol) {
          const match = matchSavedColumnName(savedCol, cols, numeric);
          if (match) {
            setSelectedColumn(match);
          } else {
            setSelectedColumn(numeric.length > 0 ? numeric[0] : cols[0]);
          }
        } else {
          setSelectedColumn((prev) => {
            if (prev) return prev;
            return numeric.length > 0 ? numeric[0] : cols[0];
          });
        }
      }
    } catch (err) {
      console.error('Error fetching data for KPI:', err);
      setError(getApiErrorMessage(err, 'Failed to load data.'));
      setColumns([]);
      setNumericColumns([]);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  }, [api, jobId, initialData?.widgetId, initialData?.selectedColumns?.[0]]);

  useEffect(() => {
    if (open && jobId) {
      fetchData();
    }
  }, [open, jobId, fetchData]);

  useEffect(() => {
    if (!open || !initialData) return;
    if (initialData.widgetName) setWidgetName(initialData.widgetName);
    if (initialData.aggregation) setMeasure(initialData.aggregation);
    if (initialData.description || initialData.notWorkingWell) {
      setSubLabel(initialData.description || initialData.notWorkingWell);
      setIsSubLabelDirty(true);
    }
    const sc = initialData.selectedColumns;
    if (Array.isArray(sc) && sc[0] && columns.length > 0) {
      const match = matchSavedColumnName(sc[0], columns, numericColumns);
      if (match) setSelectedColumn(match);
    }
  }, [open, initialData, numericColumns, columns]);

  useEffect(() => {
    if (!isSubLabelDirty && selectedColumn && measure) {
      const measureLabel = KPI_MEASURES.find(m => m.value === measure)?.label || measure;
      setSubLabel(`${measureLabel} · ${formatBusinessText(selectedColumn)}`);
    }
  }, [selectedColumn, measure, isSubLabelDirty]);

  const handleAddToDashboard = () => {
    if (!selectedColumn || !widgetName.trim()) return;
    const measureLabel = KPI_MEASURES.find(m => m.value === measure)?.label || measure;
    const payload = [{
      recommendation: widgetName.trim(),
      notWorkingWell: subLabel.trim() || `${measureLabel} · ${formatBusinessText(selectedColumn)}`,
      description: subLabel.trim() || `${measureLabel} · ${formatBusinessText(selectedColumn)}`,
      widgetName: widgetName.trim(),
      widgetType: 'kpi',
      cardType: 'KPI',
      selectedColumns: [selectedColumn],
      aggregation: measure
    }];
    onAddToDashboard(payload);
    onClose();
  };

  const canAdd = selectedColumn && widgetName.trim().length > 0;
  const measureLabel = KPI_MEASURES.find(m => m.value === measure)?.label || measure;
  const displayValue = previewValue !== null && previewValue !== undefined
    ? (Number.isInteger(previewValue) ? previewValue : Number(previewValue).toFixed(2))
    : null;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={branding.dialog?.slotProps}
      className="build-bar-chart-dialog"
    >
      <DialogTitle className="build-bar-chart-title">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Box className="build-bar-chart-title-content">
            <SpeedIcon className="build-bar-chart-icon" sx={{ fontSize: 28 }} />
            <Typography variant="h6" component="span">KPI</Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="build-bar-chart-content">
        {loading && (
          <Box className="build-bar-chart-loading">
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <>
            <Grid container spacing={2} className="build-bar-chart-inputs" sx={{ alignItems: 'flex-start' }}>
              <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 1, minHeight: '20px' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666', fontFamily: branding.fonts.primary }}
                  >
                    Card Name
                    <Box component="span" sx={{ color: '#666666', ml: 0.25 }}>*</Box>:
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  value={widgetName}
                  onChange={(e) => setWidgetName(e.target.value)}
                  placeholder="e.g. Total Revenue"
                  variant="outlined"
                  required
                  helperText="Give your card a descriptive name"
                  sx={{ mt: 0 }}
                  inputProps={{ 'aria-label': 'Card Name' }}
                  InputProps={{
                    startAdornment: <SpeedIcon sx={{ mr: 1, color: '#000000', fontSize: 20 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 1, minHeight: '20px' }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '12px', fontWeight: 600, color: '#666666' }}>
                    Numerical Column:
                  </Typography>
                </Box>
                <FormControl fullWidth sx={{ mt: 0 }}>
                  <InputLabel>Numerical Column</InputLabel>
                  <Select
                    value={selectedColumn}
                    onChange={(e) => setSelectedColumn(e.target.value)}
                    label="Numerical Column"
                    MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                  >
                    {availableColumns.map((col) => (
                      <MenuItem key={col} value={col}>
                        {formatBusinessText(col)}
                      </MenuItem>
                    ))}
                  </Select>
                  {numericColumns.length === 0 && columns.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      No numeric columns detected; all columns shown.
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 1, minHeight: '20px' }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '12px', fontWeight: 600, color: '#666666' }}>
                    Measure:
                  </Typography>
                </Box>
                <FormControl fullWidth sx={{ mt: 0 }}>
                  <InputLabel>Measure</InputLabel>
                  <Select
                    value={measure}
                    onChange={(e) => setMeasure(e.target.value)}
                    label="Measure"
                    MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                  >
                    {KPI_MEASURES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 1, minHeight: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: '12px', fontWeight: 600, color: '#666666' }}
                  >
                    Description:
                  </Typography>
                  {isSubLabelDirty && (
                    <Button
                      size="small"
                      onClick={() => {
                        setIsSubLabelDirty(false);
                        if (selectedColumn && measure) {
                          const measureLabel = KPI_MEASURES.find(m => m.value === measure)?.label || measure;
                          setSubLabel(`${measureLabel} · ${formatBusinessText(selectedColumn)}`);
                        } else {
                          setSubLabel('');
                        }
                      }}
                      sx={{ 
                        fontSize: '10px', 
                        minWidth: 'auto', 
                        p: 0, 
                        textTransform: 'none',
                        lineHeight: 1,
                        color: 'rgb(64, 121, 141)',
                        '&:hover': { background: 'transparent', textDecoration: 'underline' }
                      }}
                    >
                      Reset to default
                    </Button>
                  )}
                </Box>
                <TextField
                  fullWidth
                  value={subLabel}
                  onChange={(e) => {
                    setSubLabel(e.target.value);
                    setIsSubLabelDirty(true);
                  }}
                  placeholder="e.g. Average · Total Revenue"
                  variant="outlined"
                  sx={{ mt: 0 }}
                  inputProps={{ 'aria-label': 'Sub-Label' }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Select a numerical column and measure. The KPI will show one value (e.g. average, sum, min, max) for that column.
            </Typography>

            <Box className="build-bar-chart-preview" sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                KPI Preview
              </Typography>
              <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                {selectedColumn
                  ? `Showing ${measureLabel} of "${formatBusinessText(selectedColumn)}" from preview data.`
                  : 'Select column and measure to see preview.'}
              </Typography>
            </Box>

            {!selectedColumn || chartData.length === 0 ? (
              <Box className="bar-chart-empty" sx={{ minHeight: 280 }}>
                <Typography variant="caption" color="text.secondary">
                  {!selectedColumn ? 'Select a numerical column and measure.' : 'No preview data available.'}
                </Typography>
              </Box>
            ) : (
              <Box className="build-bar-chart-single-value">
                <Box className="build-bar-chart-single-value-card" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: '14px !important', gap: 0.5, minHeight: '120px', width: '100%', maxWidth: '300px' }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: branding.fonts.primary, textAlign: 'center' }}>
                    {widgetName || 'KPI Card'}
                  </Typography>
                  <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: 'rgb(64, 121, 141)', fontFamily: branding.fonts.primary, lineHeight: 1 }}>
                    {displayValue !== null ? displayValue : '—'}
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#9ca3af', fontFamily: branding.fonts.primary, textAlign: 'center', textTransform: 'capitalize', letterSpacing: '0.3px' }}>
                    {subLabel || `${measureLabel}${selectedColumn ? ` · ${formatBusinessText(selectedColumn)}` : ''}`}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions className="build-bar-chart-actions" sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontFamily: branding.fonts.primary }}>
          Cancel
        </Button>
        {editWidgetId && onUpdateCard ? (
          <Button
            variant="contained"
            onClick={() => onUpdateCard({ widgetName: widgetName.trim(), selectedColumns: selectedColumn ? [selectedColumn] : [], widgetType: 'kpi', aggregation: measure, description: subLabel.trim() || `${measureLabel} · ${formatBusinessText(selectedColumn)}` })}
            disabled={!canAdd}
            sx={{
              fontFamily: branding.fonts.primary,
              backgroundColor: '#000',
              '&:hover': { backgroundColor: '#333' },
              '&:disabled': { backgroundColor: '#d3d3d3', color: '#a9a9a9' }
            }}
          >
            Update card
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleAddToDashboard}
            disabled={!canAdd}
            sx={{
              fontFamily: branding.fonts.primary,
              backgroundColor: '#000',
              '&:hover': { backgroundColor: '#333' },
              '&:disabled': { backgroundColor: '#d3d3d3', color: '#a9a9a9' }
            }}
          >
            Add to Dashboard
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BuildKPI;
